/**
 * Per-photo upload screening — the wire contract (#866).
 *
 * # Why this exists, in one paragraph
 *
 * A controlled 32-call ablation established that ONE of a customer's four
 * uploaded photographs — an ordinary bath-time snapshot of an unclothed
 * infant — was responsible for essentially all of our Azure `gpt-image-2`
 * content blocks: **present → 14 of 19 calls blocked, absent → 0 of 13,
 * Fisher exact p ≈ 3e-5**. In production the same signature cost **6
 * terminally-failed books** (19 of 31 photo-bearing calls blocked). The
 * rejection is the model-level INPUT classifier (`code:
 * "moderation_blocked"`, `moderation_details.moderation_stage: "input"`,
 * `categories: ["sexual"]`), so it fires before a single pixel is drawn and no
 * amount of retrying rescues it — see `callImageGen.ts`'s CONTENT FILTER
 * block, which deliberately does not re-roll a correct detection.
 *
 * The only useful place to learn this is at UPLOAD time, while the customer is
 * still in the survey and can swap the photograph, instead of twenty minutes
 * into a paid generation.
 *
 * # 🚨 Why the verdict set is open at the wire and closed only in code
 *
 * `reason` is a plain `string`, NOT a union. The server WILL add slugs (a new
 * moderation category, a new infrastructure failure mode) and must be able to
 * do so without a client release — the repo's permissive-wire rule. Clients
 * branch on `verdict` (four values, each with a defined UI consequence) and
 * treat `reason` as an opaque key for copy lookup with a generic fallback.
 * `verdict` itself is narrowed AFTER parsing by
 * {@link toPhotoScreenVerdict}, which degrades anything unrecognised to
 * `'unknown'` — the verdict that never blocks anybody.
 */

import { z } from 'zod';

/**
 * What we learned about one photograph.
 *
 * - `pending` — enqueued or in flight. The ONLY non-terminal value.
 * - `usable` — `gpt-image-2` accepted a request carrying this photograph.
 * - `unusable` — `gpt-image-2` refused it at the input classifier. This is the
 *   one verdict the client blocks progression on.
 * - `unknown` — we could not find out (transport failure, privacy refusal,
 *   unreadable bytes). 🚨 **Fails OPEN**: it must never block the customer.
 *   The downstream `gpt-image-2` input filter is the real control and is
 *   entirely unaffected by anything this screen does or fails to do.
 */
export type PhotoScreenVerdict = 'usable' | 'unusable' | 'pending' | 'unknown';

export const PHOTO_SCREEN_VERDICTS: readonly PhotoScreenVerdict[] = [
  'usable',
  'unusable',
  'pending',
  'unknown',
];

/**
 * Narrow an arbitrary wire string to a {@link PhotoScreenVerdict}.
 *
 * Anything unrecognised becomes `'unknown'` — deliberately the fail-open
 * verdict, so a server that starts emitting a value this client has never
 * heard of degrades to "we don't know" rather than to a spurious block.
 */
export function toPhotoScreenVerdict(value: unknown): PhotoScreenVerdict {
  return typeof value === 'string' &&
    (PHOTO_SCREEN_VERDICTS as readonly string[]).includes(value)
    ? (value as PhotoScreenVerdict)
    : 'unknown';
}

/** Whether this verdict is final — i.e. re-screening would tell us nothing new. */
export function isTerminalPhotoScreenVerdict(
  verdict: PhotoScreenVerdict
): boolean {
  return verdict !== 'pending';
}

/**
 * Machine reason slugs the server emits today.
 *
 * 🚨 **This is an OPEN set, not an enum.** It is exported so the client has
 * stable keys to hang copy off, not so it can exhaustively switch on them.
 * A client meeting an unknown slug must fall back to generic copy for the
 * `verdict`.
 *
 * No customer-facing prose is ever returned by the server for these — the copy
 * lives in the client.
 */
export const PHOTO_SCREEN_REASONS = {
  /** The provider's input moderation classifier refused the photograph. */
  MODERATION_BLOCKED: 'moderation-blocked',
  /** The bytes could not be downloaded, decoded, or transcoded. */
  PHOTO_UNREADABLE: 'photo-unreadable',
  /** Anything else — transport failure, provider outage, privacy refusal. */
  SCREEN_ERROR: 'screen-error',
} as const;

export type PhotoScreenReason =
  (typeof PHOTO_SCREEN_REASONS)[keyof typeof PHOTO_SCREEN_REASONS];

export interface PhotoScreenResult {
  /**
   * The photo's identity, which is its `user-uploads` **bucket path** — the
   * same `PhotoReference.bucketPath` the client already holds for every entry
   * of `preferences.photoUrls`.
   *
   * Named `photoUrl` to match the survey field it indexes (`photoUrls`); it is
   * NOT a fetchable URL and must not be used as one.
   */
  photoUrl: string;
  verdict: PhotoScreenVerdict;
  /** Machine slug, open string (NOT a closed union — the server must be able to add values without breaking clients). */
  reason?: string;
}

export interface PhotoScreenStatusResponse {
  results: PhotoScreenResult[];
  /** True when every photo has a terminal verdict. */
  complete: boolean;
  /** True when at least one photo is `unusable` — the client blocks progression on this. */
  blocked: boolean;
}

/**
 * Wire schemas.
 *
 * `verdict` and `reason` are BOTH `z.string()` at the boundary: the repo rule
 * is to prefer a permissive wire over a closed union for any field the server
 * will add values to. Narrowing happens after parsing, via
 * {@link toPhotoScreenVerdict}.
 */
export const photoScreenResultSchema = z.object({
  photoUrl: z.string(),
  verdict: z.string(),
  reason: z.string().optional(),
});

export const photoScreenStatusResponseSchema = z.object({
  results: z.array(photoScreenResultSchema),
  complete: z.boolean(),
  blocked: z.boolean(),
});

/** Derive the two summary flags from a result set — one definition, both sides. */
export function summarizePhotoScreenResults(
  results: readonly PhotoScreenResult[]
): { complete: boolean; blocked: boolean } {
  return {
    complete: results.every((result) =>
      isTerminalPhotoScreenVerdict(toPhotoScreenVerdict(result.verdict))
    ),
    blocked: results.some(
      (result) => toPhotoScreenVerdict(result.verdict) === 'unusable'
    ),
  };
}
